import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { getLatestNews } from "../../services/newsServices";
import { toAbsolutePhotoUrl } from "../../services/newsPhotoServices";
import {
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiMaximize2,
  FiX,
} from "react-icons/fi";
import {
  FaExclamationTriangle,
  FaImage,
  FaNewspaper,
  FaRegClock,
  FaUserEdit,
} from "react-icons/fa";

const FALLBACK = "/img/no-image.jpg";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function toAbs(url) {
  const cleanUrl = String(url || "").trim();
  const absoluteUrl = cleanUrl ? toAbsolutePhotoUrl(cleanUrl) : "";

  return absoluteUrl || FALLBACK;
}

function stripHtml(html = "") {
  if (typeof window === "undefined") {
    return String(html || "").replace(/<[^>]*>/g, "");
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  return div.textContent || div.innerText || "";
}

function sanitizeHtml(html = "") {
  return DOMPurify.sanitize(html || "", {
    USE_PROFILES: {
      html: true,
    },
    FORBID_ATTR: ["style"],
  });
}

function authorLabel(news) {
  const prenom = news?.author?.prenom || "";
  const nom = news?.author?.nom || "";
  const fullName = `${prenom} ${nom}`.trim();

  return fullName || "Club";
}

function getNewsDate(news) {
  const rawDate =
    news?.published_at ||
    news?.date_publication ||
    news?.date_news ||
    news?.createdAt ||
    news?.updatedAt;

  if (!rawDate) return "";

  const date = new Date(rawDate);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function NewsSkeleton() {
  return (
    <section className="relative overflow-hidden bg-slate-50 py-20 dark:bg-slate-950 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto h-9 w-44 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-6 h-12 w-2/3 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-5 h-5 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="mt-14 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-xl dark:border-white/10 dark:bg-slate-900">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr]">
            <div className="h-80 animate-pulse bg-slate-200 dark:bg-slate-800 lg:h-[460px]" />
            <div className="space-y-4 p-6 sm:p-8 lg:p-10">
              <div className="h-8 w-40 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
              <div className="h-10 w-3/4 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
              <div className="h-4 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
              <div className="h-4 w-11/12 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
              <div className="h-4 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white/70 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
        <FaNewspaper />
      </div>

      <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        Aucune actualité disponible
      </h3>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
        Les dernières nouvelles du club apparaîtront ici dès qu’elles seront
        publiées.
      </p>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
      <div className="flex items-center justify-center gap-3">
        <FaExclamationTriangle />
        {message}
      </div>
    </div>
  );
}

export default function LatestNews() {
  const [newsList, setNewsList] = useState([]);
  const [index, setIndex] = useState(0);
  const [selectedNews, setSelectedNews] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [animDir, setAnimDir] = useState("next");
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchNews = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const latest = await getLatestNews();
        const activeNews = (Array.isArray(latest) ? latest : [])
          .filter((news) => {
            if (
              news?.is_active === undefined ||
              news?.is_active === null ||
              news?.is_active === ""
            ) {
              return true;
            }

            return (
              news.is_active === true ||
              news.is_active === 1 ||
              news.is_active === "1"
            );
          })
          .slice(0, 6);

        if (!mounted) return;

        setNewsList(activeNews);
        setIndex(0);
      } catch (error) {
        console.error("Erreur lors du chargement des actualités :", error);

        if (mounted) {
          setNewsList([]);
          setErrorMsg("Impossible de charger les dernières actualités.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchNews();

    return () => {
      mounted = false;
    };
  }, []);

  const total = newsList.length;

  const current = useMemo(() => {
    return newsList[index] || null;
  }, [newsList, index]);

  const currentImage = current ? toAbs(current.image_url) : FALLBACK;
  const currentDate = current ? getNewsDate(current) : "";

  const previewText = useMemo(() => {
    const rawText = stripHtml(current?.content || "");

    if (!rawText) {
      return "Le contenu de cette actualité sera bientôt disponible.";
    }

    if (rawText.length <= 360) {
      return rawText;
    }

    return `${rawText.slice(0, 360).trim()}...`;
  }, [current?.content]);

  const safeSelectedNewsHtml = useMemo(() => {
    return sanitizeHtml(selectedNews?.content || "");
  }, [selectedNews?.content]);

  const go = (nextIndex, direction) => {
    if (!total) return;

    setAnimDir(direction);
    setIndex((nextIndex + total) % total);
  };

  const prev = () => go(index - 1, "prev");
  const next = () => go(index + 1, "next");

  const openArticle = () => {
    if (current) {
      setSelectedNews(current);
    }
  };

  const closeArticle = () => {
    setSelectedNews(null);
  };

  const openImageModal = () => {
    if (current) {
      setSelectedImage(toAbs(current.image_url));
    }
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const onImgError = (event) => {
    if (event?.currentTarget) {
      event.currentTarget.src = FALLBACK;
    }
  };

  if (loading) return <NewsSkeleton />;

  return (
    <section
      id="latest-news"
      className="relative scroll-mt-[calc(var(--site-navbar-h)+24px)] overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28"
    >
      <style>{`
        @keyframes ln-in-next {
          from { opacity: 0; transform: translateX(22px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes ln-in-prev {
          from { opacity: 0; transform: translateX(-22px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @media (prefers-reduced-motion: reduce) {
          .ln-anim {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaNewspaper />
            Actualités du club
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Dernières{" "}
            <span className="text-primary">actualités</span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            {total
              ? "Retrouvez les dernières informations, annonces et temps forts publiés par le club."
              : "Aucune actualité pour le moment."}
          </p>
        </div>

        <div className="mt-14">
          {errorMsg && <ErrorState message={errorMsg} />}

          {!errorMsg && !current && <EmptyState />}

          {!errorMsg && current && (
            <div className="relative overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-900">
              {total > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prev}
                    className="absolute left-4 top-1/2 z-30 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-800 shadow-xl backdrop-blur-xl transition hover:scale-105 hover:text-primary dark:border-white/10 dark:bg-slate-950/80 dark:text-white md:flex"
                    aria-label="Actualité précédente"
                  >
                    <FiChevronLeft className="text-xl" />
                  </button>

                  <button
                    type="button"
                    onClick={next}
                    className="absolute right-4 top-1/2 z-30 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-800 shadow-xl backdrop-blur-xl transition hover:scale-105 hover:text-primary dark:border-white/10 dark:bg-slate-950/80 dark:text-white md:flex"
                    aria-label="Actualité suivante"
                  >
                    <FiChevronRight className="text-xl" />
                  </button>
                </>
              )}

              <div
                key={current.id || index}
                className={cn(
                  "ln-anim grid lg:grid-cols-[0.95fr_1.05fr]",
                  animDir === "next"
                    ? "[animation:ln-in-next_.32s_ease-out]"
                    : "[animation:ln-in-prev_.32s_ease-out]"
                )}
              >
                <div className="relative min-h-[320px] overflow-hidden lg:min-h-[500px]">
                  <button
                    type="button"
                    onClick={openImageModal}
                    className="block h-full w-full"
                    aria-label="Agrandir l’image"
                    title="Agrandir l’image"
                  >
                    <img
                      src={currentImage}
                      alt={current.title || "Illustration"}
                      loading="lazy"
                      decoding="async"
                      onError={onImgError}
                      className="h-full w-full object-cover transition duration-700 hover:scale-105"
                    />
                  </button>

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/25 to-transparent" />

                  <button
                    type="button"
                    onClick={openImageModal}
                    className="absolute right-5 top-5 z-20 flex h-12 w-12 items-center justify-center rounded-full border border-white/35 bg-white/95 text-slate-950 shadow-2xl backdrop-blur-xl transition hover:scale-105 hover:bg-primary hover:text-white"
                    aria-label="Agrandir l’image"
                    title="Agrandir l’image"
                  >
                    <FiMaximize2 className="text-lg" />
                  </button>

                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white/80 backdrop-blur-xl">
                        <FaUserEdit className="text-primary" />
                        {authorLabel(current)}
                      </span>

                      {currentDate && (
                        <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/15 px-4 py-2 text-xs font-bold text-white/80 backdrop-blur-xl">
                          <FaRegClock className="text-primary" />
                          {currentDate}
                        </span>
                      )}
                    </div>

                    <h3 className="mt-5 max-w-2xl font-barlow text-3xl font-extrabold leading-tight text-white drop-shadow-lg sm:text-4xl">
                      {current.title}
                    </h3>
                  </div>
                </div>

                <div className="relative flex flex-col p-6 sm:p-8 lg:p-10">
                  <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-primary/10 blur-3xl" />

                  <div className="relative flex items-center justify-between gap-4">
                    <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                      <FiFileText />
                      Article du club
                    </div>

                    {total > 1 && (
                      <div className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-500 shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                        {index + 1} / {total}
                      </div>
                    )}
                  </div>

                  <h3 className="relative mt-6 font-barlow text-3xl font-extrabold leading-tight text-slate-950 dark:text-white sm:text-4xl">
                    {current.title}
                  </h3>

                  <p className="relative mt-5 text-base leading-8 text-slate-600 dark:text-slate-300">
                    {previewText}
                  </p>

                  <div className="relative mt-8 flex flex-wrap items-center justify-between gap-3">
                    {total > 1 && (
                      <div className="flex gap-2 md:hidden">
                        <button
                          type="button"
                          onClick={prev}
                          className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-white"
                          aria-label="Actualité précédente"
                        >
                          <FiChevronLeft />
                        </button>

                        <button
                          type="button"
                          onClick={next}
                          className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-white"
                          aria-label="Actualité suivante"
                        >
                          <FiChevronRight />
                        </button>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={openArticle}
                      className="ml-auto inline-flex items-center justify-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-5 py-3 text-sm font-bold text-primary transition hover:-translate-y-0.5 hover:bg-primary hover:text-white"
                      aria-label="Lire l'article complet"
                    >
                      <FiFileText />
                      Lire la suite
                    </button>
                  </div>

                  {total > 1 && (
                    <div className="relative mt-8 flex items-center justify-center gap-2">
                      {newsList.map((news, dotIndex) => (
                        <button
                          key={news.id || dotIndex}
                          type="button"
                          onClick={() =>
                            go(dotIndex, dotIndex > index ? "next" : "prev")
                          }
                          className={cn(
                            "h-2.5 rounded-full transition-all",
                            dotIndex === index
                              ? "w-8 bg-primary"
                              : "w-2.5 bg-slate-300 hover:bg-slate-400 dark:bg-slate-700 dark:hover:bg-slate-600"
                          )}
                          aria-label={`Aller à l'actualité ${dotIndex + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-90" />
            </div>
          )}
        </div>
      </div>

      {selectedNews && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/75 px-3 py-6 backdrop-blur-sm"
          onClick={closeArticle}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl dark:bg-slate-950"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeArticle}
              className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/85 text-slate-700 shadow-lg backdrop-blur transition hover:text-primary dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-200"
              aria-label="Fermer"
            >
              <FiX className="text-xl" />
            </button>

            <div className="grid max-h-[92vh] overflow-y-auto overflow-x-hidden lg:grid-cols-[0.85fr_1.15fr]">
              <div className="relative min-h-[300px] overflow-hidden lg:min-h-full">
                <img
                  src={toAbs(selectedNews.image_url)}
                  alt={selectedNews.title || "Illustration"}
                  className="h-full w-full object-cover"
                  onError={onImgError}
                />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />

                <div className="absolute bottom-6 left-6 right-6">
                  <span className="inline-flex rounded-full border border-white/15 bg-white/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white/75 backdrop-blur-xl">
                    {authorLabel(selectedNews)}
                  </span>

                  <h2 className="mt-4 font-barlow text-3xl font-extrabold leading-tight text-white drop-shadow-xl">
                    {selectedNews.title}
                  </h2>
                </div>
              </div>

              <div className="p-6 text-slate-900 dark:text-white sm:p-8 lg:p-10">
                <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                  <FiFileText />
                  Article complet
                </div>

                <h2 className="mt-6 font-barlow text-3xl font-extrabold text-slate-950 dark:text-white">
                  {selectedNews.title}
                </h2>

                <div className="mt-5 max-h-[55vh] overflow-y-auto pr-2">
                  <div
                    className={cn(
                      "prose prose-slate max-w-none dark:prose-invert",
                      "text-sm leading-7 text-slate-600 dark:text-slate-300",
                      "prose-a:font-bold prose-a:text-primary",
                      "dark:[&_strong]:!text-white dark:[&_b]:!text-white",
                      "dark:[&_strong]:font-extrabold dark:[&_b]:font-extrabold"
                    )}
                    dangerouslySetInnerHTML={{
                      __html: safeSelectedNewsHtml,
                    }}
                  />
                </div>

                <div className="mt-8">
                  <button
                    type="button"
                    onClick={closeArticle}
                    className="inline-flex items-center justify-center rounded-full bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedImage && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/80 px-3 py-6 backdrop-blur-sm"
          onClick={closeImageModal}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative w-full max-w-6xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeImageModal}
              className="absolute -right-3 -top-3 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/90 text-slate-700 shadow-xl transition hover:text-primary dark:bg-slate-900 dark:text-slate-200"
              aria-label="Fermer"
            >
              <FiX className="text-xl" />
            </button>

            <img
              src={selectedImage}
              alt="Agrandissement"
              className="max-h-[82vh] w-full rounded-[2rem] border border-white/20 bg-black/20 object-contain shadow-2xl"
              onError={onImgError}
            />
          </div>
        </div>
      )}
    </section>
  );
}